if (!customElements.get('product-shipping-quote')) {
  customElements.define(
    'product-shipping-quote',
    class ProductShippingQuote extends HTMLElement {
      constructor() {
        super();
        this.cache = null;
        this.abortController = null;
        this.onSubmit = this.onSubmit.bind(this);
        this.onPubsubVariantChange = this.onPubsubVariantChange.bind(this);
        this.onDocumentVariantChange = this.onDocumentVariantChange.bind(this);
      }

      connectedCallback() {
        this.form = this.querySelector('form');
        this.postcodeInput = this.querySelector('[data-shipping-postcode]');
        this.submitBtn = this.querySelector('[data-shipping-submit]');
        this.resultEl = this.querySelector('[data-shipping-result]');
        this.priceEl = this.querySelector('[data-shipping-price]');
        this.metaEl = this.querySelector('[data-shipping-meta]');
        this.errorEl = this.querySelector('[data-shipping-error]');
        this.disclaimerEl = this.querySelector('[data-shipping-disclaimer]');
        this.sectionId = this.dataset.sectionId;
        this.defaultButtonLabel = this.dataset.buttonLabel || 'Calculate shipping';
        this.loadingLabel = this.dataset.loadingLabel || 'Calculating…';

        this.form?.addEventListener('submit', this.onSubmit);

        if (window.FoxTheme?.pubsub) {
          this.unsubscribeVariant = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
            this.onPubsubVariantChange
          );
        }

        document.addEventListener('variant:changed', this.onDocumentVariantChange);
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.onSubmit);
        document.removeEventListener('variant:changed', this.onDocumentVariantChange);
        if (this.unsubscribeVariant) this.unsubscribeVariant();
        this.abortController?.abort();
      }

      onPubsubVariantChange(payload) {
        const sectionId = payload?.data?.sectionId;
        if (sectionId && sectionId !== this.sectionId) return;
        this.handleVariantUpdate(payload?.data?.variant);
      }

      onDocumentVariantChange(event) {
        this.handleVariantUpdate(event.detail?.variant);
      }

      handleVariantUpdate(variant) {
        this.clearResult();
        this.cache = null;

        if (variant?.id) {
          this.dataset.variantId = String(variant.id);
          return;
        }

        const id = this.getVariantId();
        if (id) this.dataset.variantId = String(id);
      }

      getVariantId() {
        const form = document.getElementById(`product-form-${this.sectionId}`);
        const input = form?.querySelector('input.product-variant-id, input[name="id"]');
        if (input?.value) return Number(input.value);

        const pickup = document.querySelector('pickup-availability[data-variant-id]');
        if (pickup?.dataset.variantId) return Number(pickup.dataset.variantId);

        const fromDataset = Number(this.dataset.variantId);
        return fromDataset || null;
      }

      clearResult() {
        if (this.resultEl) {
          this.resultEl.hidden = true;
        }
        if (this.priceEl) {
          this.priceEl.textContent = '';
        }
        if (this.metaEl) {
          this.metaEl.hidden = true;
          this.metaEl.textContent = '';
        }
        if (this.errorEl) {
          this.errorEl.hidden = true;
          this.errorEl.textContent = '';
        }
      }

      setLoading(loading) {
        if (!this.submitBtn) return;
        this.submitBtn.disabled = loading;
        this.submitBtn.classList.toggle('btn--loading', loading);
        const text = this.submitBtn.querySelector('[data-shipping-submit-text]');
        if (text) {
          text.textContent = loading ? this.loadingLabel : this.defaultButtonLabel;
        }
      }

      formatMoney(amount) {
        const value = Number(amount);
        if (Number.isNaN(value)) return '$0.00';
        return `$${value.toLocaleString('en-NZ', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      }

      showError(message) {
        if (this.errorEl) {
          this.errorEl.hidden = false;
          this.errorEl.textContent = message;
        }
      }

      showSuccess(data) {
        const rate = data.rates[0];
        const money = this.formatMoney(rate.price);

        if (this.resultEl) {
          this.resultEl.hidden = false;
        }
        if (this.priceEl) {
          this.priceEl.textContent = money;
        }
        if (this.metaEl) {
          const parts = [rate.name, data.zone_label].filter(Boolean);
          this.metaEl.textContent = parts.join(' · ');
          this.metaEl.hidden = parts.length === 0;
        }
      }

      async onSubmit(event) {
        event.preventDefault();
        this.clearResult();

        const postcode = String(this.postcodeInput?.value || '').trim();
        if (!/^\d{4}$/.test(postcode)) {
          this.showError('Please enter a valid 4-digit NZ postcode.');
          return;
        }

        const variantId = this.getVariantId();
        if (!variantId) {
          this.showError('Select a product option first.');
          return;
        }

        const cacheKey = `${variantId}:${postcode}`;
        if (this.cache?.key === cacheKey) {
          this.showSuccess(this.cache.data);
          return;
        }

        this.setLoading(true);
        this.abortController?.abort();
        this.abortController = new AbortController();

        try {
          const res = await fetch('/apps/harbour-shipping/quote', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              variant_id: variantId,
              postcode,
              country: 'NZ',
              quantity: 1,
            }),
            signal: this.abortController.signal,
          });

          const raw = await res.text();
          let data;
          try {
            data = raw ? JSON.parse(raw) : {};
          } catch {
            throw new Error(
              'Shipping service is unavailable. Please try again later or confirm at checkout.'
            );
          }

          if (!res.ok || !data.ok) {
            throw new Error(
              data.message || 'Unable to calculate shipping right now. Please try again later.'
            );
          }

          if (!Array.isArray(data.rates) || data.rates.length === 0) {
            throw new Error('No shipping rate is available for this product and postcode.');
          }

          this.cache = { key: cacheKey, data };
          this.showSuccess(data);
        } catch (err) {
          if (err.name === 'AbortError') return;
          this.showError(
            err.message || 'Unable to calculate shipping right now. Please try again later.'
          );
        } finally {
          this.setLoading(false);
        }
      }
    }
  );
}
